import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { StackedColumns, type ColumnSeries } from '../components/charts/StackedColumns';
import { SafetyActivityPanel } from '../components/SafetyActivity';
import { SafetySiteLeaderboard } from '../components/SafetyLeaderboard';
import { SafetyDigestPanel } from '../components/SafetyDigestPanel';

/**
 * The safety dashboard — the EHS view of the module.
 *
 * Not "what is on this frame" (that is the monitor) but "how are we doing":
 * events over time by severity, what kind of thing keeps happening, and where.
 * The trend answers better-or-worse; the breakdowns answer what and where; the
 * open count says how much is still un-triaged.
 */

interface Dashboard {
  window: string;
  buckets: Array<{ t: string; avgLatencyMs: number; critical: number; warning: number; info: number }>;
  byKind: Array<{ kind: string; count: number }>;
  byCamera: Array<{ camera: string; count: number }>;
  totals: { total: number; critical: number; open: number };
}

const SEVERITY_SERIES: readonly ColumnSeries[] = [
  { key: 'critical', label: 'Critical', color: 'var(--bad)' },
  { key: 'warning', label: 'Warning', color: 'var(--warn)' },
  { key: 'info', label: 'Info', color: 'var(--teal)' },
];

const KIND_LABEL: Record<string, string> = {
  ppe_violation: 'PPE violation',
  fire: 'Fire / smoke',
  near_miss: 'Near miss',
  intrusion: 'Zone intrusion',
  occupancy: 'Occupancy',
};
const KIND_COLOUR: Record<string, string> = {
  ppe_violation: 'var(--warn)',
  fire: 'var(--bad)',
  near_miss: 'var(--violet)',
  intrusion: 'var(--bad)',
  occupancy: 'var(--teal)',
};

export function SafetyDashboardView() {
  const [data, setData] = useState<Dashboard | null>(null);
  const [window, setWindow] = useState<'24h' | '7d'>('24h');
  const [cameras, setCameras] = useState<Array<{ id: number; name: string }>>([]);
  useEffect(() => {
    fetch('/api/safety/cameras').then((r) => (r.ok ? r.json() : [])).then(setCameras).catch(() => undefined);
  }, []);

  const load = useCallback(() => {
    fetch(`/api/safety/dashboard?window=${window}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => undefined);
  }, [window]);
  useEffect(() => load(), [load]);

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 className="view-title">Safety dashboard</h1>
        <div className="row" style={{ gap: 8 }}>
          <Link to="/safety" style={{ fontSize: 13 }}>Monitor</Link>
          <Link to="/safety/events" style={{ fontSize: 13 }}>Events →</Link>
        </div>
      </div>

      <div className="row" style={{ marginBottom: 16, gap: 6 }}>
        {(['24h', '7d'] as const).map((wv) => (
          <button key={wv} className={`btn sm${window === wv ? ' primary' : ''}`} onClick={() => setWindow(wv)}>
            {wv === '24h' ? 'Last 24 hours' : 'Last 7 days'}
          </button>
        ))}
      </div>

      {!data ? (
        <div className="panel spinner">Loading…</div>
      ) : data.totals.total === 0 ? (
        <div className="panel muted">No safety events in this window.</div>
      ) : (
        <div className="grid" style={{ gap: 20 }}>
          <div className="row">
            <Tile label="Events" value={data.totals.total} tone="var(--accent)" />
            <Tile label="Critical" value={data.totals.critical} tone={data.totals.critical > 0 ? 'var(--bad)' : 'var(--teal)'} />
            <Tile label="Open" value={data.totals.open} tone={data.totals.open > 0 ? 'var(--warn)' : 'var(--teal)'} />
          </div>

          <div className="panel">
            <strong>Events over time</strong>
            <p className="muted" style={{ fontSize: 12, margin: '4px 0 12px' }}>By severity, per {window === '7d' ? '6 hours' : 'hour'}.</p>
            <StackedColumns
              buckets={data.buckets}
              series={SEVERITY_SERIES}
              emptyMessage="No events in this window."
              ariaLabel="Safety events by severity over time"
            />
          </div>

          <div className="grid" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)' }}>
            <div className="panel">
              <strong>By type</strong>
              <BarList
                rows={data.byKind.map((r) => ({ label: KIND_LABEL[r.kind] ?? r.kind, count: r.count, colour: KIND_COLOUR[r.kind] ?? 'var(--accent)' }))}
              />
            </div>
            <div className="panel">
              <strong>By camera</strong>
              <BarList rows={data.byCamera.map((r) => ({ label: r.camera, count: r.count, colour: 'var(--accent)' }))} />
            </div>
          </div>
        </div>
      )}

      <SafetySiteLeaderboard apiBase="/api/safety" />
      <SafetyActivityPanel apiBase="/api/safety" cameras={cameras} />
      <SafetyDigestPanel />
    </div>
  );
}

function BarList({ rows }: { rows: Array<{ label: string; count: number; colour: string }> }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  if (rows.length === 0) return <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>Nothing.</div>;
  return (
    <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
      {rows.map((r, i) => (
        <div key={i}>
          <div className="row" style={{ justifyContent: 'space-between', fontSize: 13, marginBottom: 3 }}>
            <span>{r.label}</span>
            <span className="muted" style={{ fontVariantNumeric: 'tabular-nums' }}>{r.count}</span>
          </div>
          <div style={{ height: 8, background: 'var(--bg-panel-2)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: `${(r.count / max) * 100}%`, height: '100%', background: r.colour, borderRadius: 4 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Tile({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="stat-tile" style={{ flex: '1 1 150px', ['--tone' as string]: tone } as React.CSSProperties}>
      <div className="stat-value" style={{ fontSize: 32 }}>{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}
